import { useQuery } from '@tanstack/react-query';
import { monitoringAPI } from '../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// [2026-03-21] 요금제 한도 대비 사용량 프로그레스 바
const UsageBar = ({ label, current, max }: { label: string; current: number; max: number }) => {
  if (max === -1) return <div className="text-xs text-gray-400">{label}: 무제한</div>;
  if (max === 0) return <div className="text-xs text-gray-400">{label}: 미지원</div>;
  const pct = Math.min((current / max) * 100, 100);
  const color = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-yellow-500' : 'bg-blue-500';
  return (
    <div className="mb-2">
      <div className="flex justify-between text-xs text-gray-500 mb-1">
        <span>{label}</span>
        <span>{current.toLocaleString()} / {max.toLocaleString()}</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div className={`${color} h-2 rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

export function MonitoringTenantsPage() {
  const { data: tenantData, isLoading } = useQuery({
    queryKey: ['monitoring', 'tenant-usage'],
    queryFn: () => monitoringAPI.tenantUsage(),
  });

  const tenants = tenantData?.data || [];

  const chartData = tenants.map((t: any) => ({
    name: t.tenant_name.length > 10 ? t.tenant_name.slice(0, 10) + '...' : t.tenant_name,
    prompt: t.prompt_tokens,
    completion: t.completion_tokens,
  }));

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">테넌트별 사용량</h1>
        <p className="text-gray-600">각 테넌트의 API 사용량을 비교합니다.</p>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">로딩 중...</div>
      ) : (
        <>
          {/* 차트 */}
          {chartData.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6 mb-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">토큰 사용량 비교</h2>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="prompt" name="Prompt" fill="#3B82F6" stackId="tokens" />
                    <Bar dataKey="completion" name="Completion" fill="#10B981" stackId="tokens" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* 테이블 */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">테넌트</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">요금제</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">사용량 / 한도</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">총 토큰</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">예상 비용</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {tenants.map((t: any) => {
                  const cost = (t.prompt_tokens * 0.15 + t.completion_tokens * 0.60) / 1_000_000;
                  return (
                    <tr key={t.tenant_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium text-gray-900">{t.tenant_name}</td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                          {t.plan_name || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 min-w-[220px]">
                        <UsageBar label="메시지" current={t.messages} max={t.max_messages} />
                        <UsageBar label="문서" current={t.documents} max={t.max_documents} />
                        <UsageBar label="크롤링" current={t.crawl_page_count} max={t.max_crawl_pages} />
                      </td>
                      <td className="px-6 py-4 font-medium text-gray-900">{t.total_tokens.toLocaleString()}</td>
                      <td className="px-6 py-4 text-orange-600">${cost.toFixed(4)}</td>
                    </tr>
                  );
                })}
                {tenants.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-gray-400">데이터가 없습니다.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
